/* Site header: scrolled state, mega panel toggles, mobile drawer. */

(() => {
  'use strict';

  class AntartiHeader extends HTMLElement {
    connectedCallback() {
      this.drawer = this.querySelector('#MenuDrawer');
      this.burger = this.querySelector('[data-drawer-open]');
      this.items = Array.from(this.querySelectorAll('.site-header__item.has-mega'));
      this.transparent = this.classList.contains('site-header--transparent');

      /* Expose the real header height to the rest of the theme (scroll padding, drawers).
         The bar's own height comes from --header-bar-h, never from this value, so measuring
         cannot feed back into the size it measures. */
      this.measure = () => document.documentElement.style.setProperty('--header-h', `${this.offsetHeight}px`);
      this.measure();
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.measure);
        this.resizeObserver.observe(this);
      } else {
        window.addEventListener('resize', this.measure, { passive: true });
      }

      /* Solid background once the page has scrolled past the top. */
      this.onScroll = () => {
        if (this.ticking) return;
        this.ticking = true;
        requestAnimationFrame(() => {
          /* Over a pinned hero (the showcase, or the Spotlight film) the header stays transparent until it unpins. */
          const hero = this.transparent
            ? document.querySelector('#MainContent > .shopify-section:first-child :is(antarti-hero.is-pinned, antarti-seq)')
            : null;
          const limit = hero ? Math.max(40, hero.offsetHeight - window.innerHeight) : 40;
          this.classList.toggle('is-scrolled', window.scrollY > limit);
          this.ticking = false;
        });
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.onScroll();

      /* Mega panels: hover is CSS; keyboard focus entering an item opens it; the chevron
         toggles for touch and keyboard; Escape closes and keeps focus on the link. */
      this.onToggle = (e) => {
        const btn = e.target.closest('[data-mega-toggle]');
        if (!btn) return;
        const item = btn.closest('.site-header__item');
        const open = !item.classList.contains('is-open');
        this.closeAll();
        if (open) this.open(item);
      };
      this.onFocusIn = (e) => {
        const item = e.target.closest('.site-header__item.has-mega');
        if (!item) {
          this.closeAll();
          return;
        }
        /* The chevron's own click opens and closes its panel; opening here as well would
           let the click that follows (Chrome/Firefox focus buttons on press) close it again. */
        if (e.target.closest('[data-mega-toggle]')) {
          if (!item.classList.contains('is-open')) this.closeAll();
          return;
        }
        /* Open only when focus arrives from outside the item, so Escape then Tab
           (link â†’ chevron) doesn't reopen what the person just closed. */
        const fromInside = e.relatedTarget && item.contains(e.relatedTarget);
        if (!item.classList.contains('is-open') && !e.target.closest('.mega') && !fromInside) {
          this.closeAll();
          this.open(item);
        }
      };
      this.onKey = (e) => {
        if (e.key !== 'Escape') return;
        const item = e.target.closest('.site-header__item.has-mega');
        this.closeAll();
        if (item) item.querySelector('.site-header__link')?.focus();
      };
      this.onFocusOut = (e) => {
        if (!this.contains(e.relatedTarget)) this.closeAll();
      };
      this.onOutside = (e) => {
        if (!this.contains(e.target)) this.closeAll();
      };
      /* Escape anywhere dismisses a panel shown by hover, without moving pointer or focus
         (WCAG 1.4.13); it stays hidden until the pointer leaves that item. */
      this.onDocKey = (e) => {
        if (e.key !== 'Escape') return;
        this.items.forEach((item) => {
          if (!item.matches(':hover') || item.classList.contains('is-dismissed')) return;
          item.classList.add('is-dismissed');
          item.addEventListener('pointerleave', () => item.classList.remove('is-dismissed'), { once: true });
          this.closeAll();
        });
      };
      this.addEventListener('click', this.onToggle);
      this.addEventListener('focusin', this.onFocusIn);
      this.addEventListener('keydown', this.onKey);
      this.addEventListener('focusout', this.onFocusOut);
      document.addEventListener('pointerdown', this.onOutside);
      document.addEventListener('keydown', this.onDocKey);

      /* Drawer */
      this.openDrawer = () => {
        if (!this.drawer) return;
        this.drawer.showModal();
        this.burger.setAttribute('aria-expanded', 'true');
      };
      this.onDrawerClose = () => this.burger.setAttribute('aria-expanded', 'false');
      this.onDrawerClick = (e) => {
        if (e.target.closest('[data-drawer-close]')) this.drawer.close();
        /* Click on the backdrop (outside the drawer's box) closes it too. */
        if (e.target === this.drawer) {
          const r = this.drawer.getBoundingClientRect();
          if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) this.drawer.close();
        }
      };
      this.burger?.addEventListener('click', this.openDrawer);
      this.drawer?.addEventListener('close', this.onDrawerClose);
      this.drawer?.addEventListener('click', this.onDrawerClick);
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
      window.removeEventListener('resize', this.measure);
      window.removeEventListener('scroll', this.onScroll);
      document.removeEventListener('pointerdown', this.onOutside);
      document.removeEventListener('keydown', this.onDocKey);
      this.burger?.removeEventListener('click', this.openDrawer);
      this.drawer?.removeEventListener('close', this.onDrawerClose);
      this.drawer?.removeEventListener('click', this.onDrawerClick);
    }

    open(item) {
      item.classList.remove('is-dismissed');
      item.classList.add('is-open');
      item.querySelector('[data-mega-toggle]')?.setAttribute('aria-expanded', 'true');
    }

    closeAll() {
      this.items.forEach((item) => {
        item.classList.remove('is-open');
        item.querySelector('[data-mega-toggle]')?.setAttribute('aria-expanded', 'false');
      });
    }
  }

  (window.antarti?.define || ((n, c) => customElements.get(n) || customElements.define(n, c)))('antarti-header', AntartiHeader);

  /* Localisation forms submit only from their Update button (no auto-submit on change: WCAG 3.2.2). */
})();
